import {
  verdictsResponseSchema,
  vocabularyPackSchema,
  type VerdictsResponse,
  type VocabularyPack,
} from '@console/types'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../api.js'

/**
 * THE SERVED VOCABULARY, not a declared one.
 *
 * Nothing in this repository should contain a string that is also a verdict
 * label, a predicate id or a fact-type name. That is CON-003's first *Done
 * when* item and it is checkable by grep, which is the point: the failure it
 * prevents is a predicate renamed in `QuotingPolicy.cs` while the console goes
 * on rendering the old word, and nobody notices because nothing breaks.
 *
 * Cached for a long while deliberately. This changes when the platform
 * republishes a pack — rare, and never mid-session — so re-fetching it per
 * screen would be a round trip in front of every render for a value that has
 * not moved in weeks.
 */
export function useVerdicts() {
  return useQuery<VerdictsResponse>({
    queryKey: ['reference', 'verdicts'],
    queryFn: () => apiGet('/reference/verdicts', verdictsResponseSchema),
    staleTime: 5 * 60_000,
  })
}

export function useVocabularyPack() {
  return useQuery<VocabularyPack>({
    queryKey: ['reference', 'vocabulary'],
    queryFn: () => apiGet('/reference/vocabulary', vocabularyPackSchema),
    staleTime: 5 * 60_000,
  })
}

/**
 * A verdict's served label, or the raw value when the vocabulary has not
 * arrived yet.
 *
 * Falling back to the value is deliberate and is NOT a hardcoded label: it is
 * the platform's own identifier, shown plainly, which is honest about the fact
 * that the console does not yet know what the platform calls it. Inventing a
 * label here — "Doesn't match", say — would be exactly the drift this hook
 * exists to prevent, and it would look correct right up until the day it was
 * not.
 */
export function verdictLabel(verdicts: VerdictsResponse | undefined, value: string): string {
  return verdicts?.verdicts.find((v) => v.value === value)?.label ?? value
}
